import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Button, ConfigProvider, Menu, Tooltip, Typography } from 'antd';
import type { NavigateFunction } from 'react-router-dom';
import {
  ALL_SPACES_KEY,
  EllipsisRowTooltip,
  FilteredBuilding,
  FilteredFloor,
  FilteredSite,
  MENU_STYLE,
  MenuItem,
  RoomZoneItem,
  SIDEBAR_TREE_THEME,
  collectAllKeys,
  collectChainOpenKeys,
  findAncestorChain,
  isInAllFloorsView,
  markSelected,
  maybeChildren,
  trimToChain,
  useTreeOpenKeys,
} from './shared';
import { Icon } from '../../primitives/icon/Icon';
import { MiddleTruncatedText } from '@/components/primitives/MiddleTruncatedText';
import { BuildingEditMenu } from '../BuildingEditMenu';
import { SiteEditMenu } from '../SiteEditMenu';
import { CreateDropdownButton } from '../CreateDropdownButton';
import { updateUrlParams } from '../utils';

interface DevicesTreeProps {
  sites: FilteredSite[];
  filterValue: string;
  search: string;
  navigate: NavigateFunction;
}

/**
 * Devices' All Spaces tree.
 *
 * Single antd Menu with every level (Site / Building / Floor / Room / Zone)
 * rendered as a SubMenu or leaf. Above the Menu sits an `All Spaces` row
 * styled like a section header — selectable, clears every space-scoping URL
 * param. Site / Building / Floor / Room / Zone are all selectable; clicking
 * a level writes the matching URL param and the device list filters.
 */
export const DevicesTree: React.FC<DevicesTreeProps> = ({
  sites,
  filterValue,
  search,
  navigate,
}) => {
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollTopRef = useRef(0);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      scrollTopRef.current = el.scrollTop;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);
  // Restores the user's scroll position after antd Menu re-renders that
  // would otherwise reset scrollTop to 0. No dep array because we want
  // to re-assert scrollTop after any render — the equality guard skips
  // the redundant DOM write when the value already matches.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || scrollTopRef.current === 0) return;
    if (el.scrollTop === scrollTopRef.current) return;
    el.scrollTop = scrollTopRef.current;
  });

  // Selection is purely derived from the URL — no other mutator. No space
  // params → All Spaces is the active selection.
  const selectedKey = useMemo<string>(() => {
    const params = new URLSearchParams(search);
    const siteId = params.get('siteId');
    const buildingId = params.get('buildingId');
    const roomId = params.get('roomId');
    const zoneId = params.get('zoneId');
    const floorId = params.get('spaceId');
    if (buildingId) return buildingId;
    if (roomId) return roomId;
    if (zoneId) return zoneId;
    if (floorId) return floorId;
    if (siteId) return siteId;
    return ALL_SPACES_KEY;
  }, [search]);

  const items = useMemo<MenuItem[]>(() => {
    return sites.map((site) => buildSiteItem(site, { filterValue, navigate }));
  }, [sites, filterValue, navigate]);

  // Auto-expand to selection (skip when nothing or when All Spaces is active).
  useEffect(() => {
    if (!selectedKey || selectedKey === ALL_SPACES_KEY) return;
    const chain = findAncestorChain(items, selectedKey);
    if (!chain || chain.length === 0) return;
    const ancestorsOnly = chain.slice(0, -1);
    if (ancestorsOnly.length === 0) return;
    setOpenKeys((prev) => {
      const merged = Array.from(new Set([...prev, ...ancestorsOnly]));
      if (merged.length === prev.length) return prev;
      return merged;
    });
  }, [selectedKey, items]);

  // `selectedKey` always returns a non-empty value (falls back to
  // `ALL_SPACES_KEY` when no space-scoping URL params are set).
  const selectedKeys = useMemo(() => [selectedKey], [selectedKey]);

  // Hoist `markSelected` for the per-site full subtree out of the
  // `items.map` so we don't recursively clone every node + hand antd Menu
  // a fresh `items` reference on every render. The chain Menu (rendered
  // only when the site is closed AND has an internal selection) builds
  // its own per-iteration `chain` from `trimToChain` and can't be
  // pre-memoized in the same pass.
  const markedItems = useMemo(
    () => markSelected(items, selectedKey) ?? items,
    [items, selectedKey],
  );

  const handleShowAllFloors = useCallback(() => {
    const next: string[] = [];
    items.forEach((site) => {
      next.push(site.key);
      site.children?.forEach((building) => next.push(building.key));
    });
    setOpenKeys(next);
  }, [items]);

  // Centralized derivations: scaffolding keys + collapse-all + search-aware
  // auto-expand (which respects user-driven closes during the search).
  const { openKeysWithSearch, handleCollapseAllRooms, collapseAllRoomsVisible, recordUserClose } =
    useTreeOpenKeys({ items, filterValue, openKeys, setOpenKeys });

  const showAllFloorsVisible = useMemo(
    () => !isInAllFloorsView(items, openKeysWithSearch),
    [items, openKeysWithSearch],
  );

  return (
    <div
      ref={scrollRef}
      className="sidebar-devices-tree overflow-auto sidebar-scroll"
      // 200px bottom padding so the last few rows aren't flush against the
      // viewport bottom — gives the user breathing room to click / hover
      // the deepest items in the tree.
      style={{ maxHeight: 'calc(100vh - 250px)', paddingBottom: 200 }}
    >
      <ConfigProvider theme={SIDEBAR_TREE_THEME}>
        <AllSpacesRow
          selected={selectedKey === ALL_SPACES_KEY}
          onClick={() => updateUrlParams(navigate, {})}
          showAllFloorsVisible={showAllFloorsVisible}
          onShowAllFloors={handleShowAllFloors}
          collapseAllRoomsVisible={collapseAllRoomsVisible}
          onCollapseAllRooms={handleCollapseAllRooms}
        />
        {items.map((site, siteIdx) => {
          // Per-site Menu rendering so a chain-breadcrumb Menu can be
          // injected directly beneath a closed Site that contains the
          // selection — same pattern as Monitor / Studio.
          const siteKeys = collectAllKeys(site);
          const siteOpen = openKeysWithSearch.filter((k) => siteKeys.has(k));
          const isClosed = !openKeysWithSearch.includes(String(site.key));
          // Drill past the trimmed Site so the chain Menu starts at
          // Building — see MonitorTree comment. Skip the chain entirely
          // when the user selected the Site itself (only relevant in
          // Devices where Site is selectable) — otherwise collapsing
          // would still re-render the site's first Building as a chain
          // and look like the row didn't collapse.
          const hasInternalSelection = selectedKey !== String(site.key);
          const trimmedSite =
            isClosed && hasInternalSelection ? trimToChain(site, selectedKey) : null;
          const chain = trimmedSite?.children?.[0] ?? null;
          const chainOpen = chain ? collectChainOpenKeys(chain, selectedKey) : [];
          const markedSite = markedItems[siteIdx] ?? site;
          return (
            <React.Fragment key={String(site.key)}>
              <Menu
                data-id="sidebar-menu"
                mode="inline"
                inlineIndent={16}
                items={[markedSite]}
                selectedKeys={selectedKeys}
                style={MENU_STYLE}
                openKeys={siteOpen}
                onOpenChange={(ks) => {
                  // While searching, record keys the user explicitly
                  // collapsed so the search auto-expand union doesn't
                  // immediately re-open them.
                  if (filterValue) {
                    const next = new Set(ks);
                    siteOpen.forEach((k) => {
                      if (!next.has(k)) recordUserClose(String(k));
                    });
                  }
                  setOpenKeys((prev) => [...prev.filter((k) => !siteKeys.has(k)), ...ks]);
                }}
              />
              {chain && (
                <Menu
                  // `paddingLeft: 16` keeps Building aligned with its
                  // natural position under an expanded Site (matches one
                  // level of `inlineIndent={16}` from the edge).
                  data-id="sidebar-chain-menu"
                  mode="inline"
                  inlineIndent={16}
                  items={markSelected([chain], selectedKey)}
                  selectedKeys={selectedKeys}
                  style={{ ...MENU_STYLE, paddingLeft: 16 }}
                  openKeys={chainOpen}
                  onOpenChange={() => {}}
                  className="sidebar-chain-menu"
                />
              )}
              {siteIdx < items.length - 1 && <div style={{ height: 8 }} />}
            </React.Fragment>
          );
        })}
      </ConfigProvider>
    </div>
  );
};

// ============================================================================
// All Spaces row — matches Monitor/Studio's "All Spaces" section header
// (Typography.Text strong type="secondary" at text-xs, padding 6px 12px),
// but the label area is also a button: clicking it clears every
// space-scoping URL param. The right side keeps "Show all floors" (when any
// floor is expanded) and the "+" Create dropdown. Both right-side controls
// stop click propagation so they don't trigger the All Spaces selection.
// ============================================================================

const AllSpacesRow: React.FC<{
  selected: boolean;
  onClick: () => void;
  showAllFloorsVisible: boolean;
  onShowAllFloors: () => void;
  collapseAllRoomsVisible: boolean;
  onCollapseAllRooms: () => void;
}> = ({
  selected,
  onClick,
  showAllFloorsVisible,
  onShowAllFloors,
  collapseAllRoomsVisible,
  onCollapseAllRooms,
}) => {
  return (
    // Match Site SubMenu padding (6px 12px) so the All Spaces label aligns
    // vertically with the Site labels below — no 1–2px drift.
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onClick();
      }}
      className={
        'sidebar-all-spaces-row flex items-center justify-between cursor-pointer my-0.5' +
        (selected ? ' sidebar-all-spaces-row-selected' : '')
      }
      style={{ padding: '6px 12px' }}
    >
      <Typography.Text strong className="text-xs text-content-primary">
        All Spaces
      </Typography.Text>
      <span style={{ display: 'inline-flex', gap: 4 }} onClick={(e) => e.stopPropagation()}>
        {collapseAllRoomsVisible && (
          <Tooltip title="Collapse all rooms">
            <Button
              type="text"
              size="small"
              icon={<Icon name="icon-layercollapseoutlined" size={16} />}
              onClick={onCollapseAllRooms}
              aria-label="Collapse all rooms"
              data-id="collapse-all-rooms"
            />
          </Tooltip>
        )}
        {showAllFloorsVisible && (
          <Tooltip title="Show all floors">
            <Button
              type="text"
              size="small"
              icon={<Icon name="flooronlyoutlined" size={16} />}
              onClick={onShowAllFloors}
              aria-label="Show all floors"
              data-id="show-all-floors"
            />
          </Tooltip>
        )}
        <CreateDropdownButton variant="icon" />
      </span>
    </div>
  );
};

// ============================================================================
// Item builders — Devices variant. Site, Building, Floor, Room and Zone are
// all selectable; clicking writes the matching URL param. Site click also
// triggers antd's SubMenu toggle so the row both selects and expands.
// ============================================================================

function buildSiteItem(
  site: FilteredSite,
  ctx: { filterValue: string; navigate: NavigateFunction },
): MenuItem {
  return {
    key: site.id,
    label: (
      <EllipsisRowTooltip name={site.name}>
        {/* Clicking the label selects the Site (writes `?siteId=…`). The
            click also bubbles to the SubMenu title and toggles expand. */}
        <div
          className="group flex w-full items-center overflow-hidden"
          onClick={() => updateUrlParams(ctx.navigate, { siteId: site.id })}
        >
          <div className="flex items-center justify-between min-w-0 flex-1 mr-1">
            <span className="flex items-center min-w-0 overflow-hidden">
              <Icon name="icon-siteoutlined" className="mr-2 shrink-0" />
              <MiddleTruncatedText
                text={site.name}
                match={ctx.filterValue}
                className="flex-1 min-w-0"
              />
            </span>
            <span
              className="opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity"
              onClick={(e) => e.stopPropagation()}
              role="presentation"
            >
              <SiteEditMenu id={site.id} blockDelete={(site.buildings ?? []).length > 0} />
            </span>
          </div>
        </div>
      </EllipsisRowTooltip>
    ),
    ...maybeChildren(site.buildings.map((b) => buildBuildingItem(b, ctx))),
  };
}

function buildBuildingItem(
  building: FilteredBuilding,
  ctx: { filterValue: string; navigate: NavigateFunction },
): MenuItem {
  return {
    key: building.id,
    label: (
      <EllipsisRowTooltip name={building.name}>
        <div
          className="group flex w-full items-center overflow-hidden text-ellipsis hover:rounded-r-full"
          onClick={() => updateUrlParams(ctx.navigate, { buildingId: building.id })}
        >
          <div className="flex items-center justify-between min-w-0 flex-1 mr-1">
            <span className="flex items-center min-w-0 overflow-hidden">
              <Icon name="building" className="mr-2 shrink-0" />
              <MiddleTruncatedText
                text={building.name}
                match={ctx.filterValue}
                className="flex-1 min-w-0"
              />
            </span>
            <span className="opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
              <BuildingEditMenu id={building.id} blockDelete={building.floors.length > 0} />
            </span>
          </div>
        </div>
      </EllipsisRowTooltip>
    ),
    ...maybeChildren(building.floors.map((f) => buildFloorItem(f, ctx))),
  };
}

function buildFloorItem(
  floor: FilteredFloor,
  ctx: { filterValue: string; navigate: NavigateFunction },
): MenuItem {
  return {
    key: floor.id,
    label: (
      <EllipsisRowTooltip name={floor.name}>
        <div
          className="flex items-center overflow-hidden text-ellipsis hover:rounded-r-full"
          onClick={() => updateUrlParams(ctx.navigate, { floorId: floor.id })}
        >
          <span className="flex items-center min-w-0 overflow-hidden">
            <Icon name="layer" className="mr-2" />
            <MiddleTruncatedText
              text={floor.name}
              match={ctx.filterValue}
              className="flex-1 min-w-0 mr-4"
            />
          </span>
        </div>
      </EllipsisRowTooltip>
    ),
    ...maybeChildren(floor.roomszones.map((item) => buildRoomZoneItem(floor, item, ctx))),
  };
}

function buildRoomZoneItem(
  floor: FilteredFloor,
  item: RoomZoneItem,
  ctx: { filterValue: string; navigate: NavigateFunction },
): MenuItem {
  const onClick = () =>
    item.kind === 'room'
      ? updateUrlParams(ctx.navigate, { floorId: floor.id, roomId: item.id })
      : updateUrlParams(ctx.navigate, { floorId: floor.id, zoneId: item.id });
  const result: MenuItem = {
    key: item.id,
    label: (
      <EllipsisRowTooltip name={item.name}>
        <div
          className="flex items-center overflow-hidden whitespace-nowrap text-ellipsis"
          onClick={onClick}
        >
          <span className="flex items-center min-w-0 overflow-hidden">
            <Icon name={item.kind === 'room' ? 'room' : 'zone'} className="mr-2" />
            <MiddleTruncatedText
              text={item.name}
              match={ctx.filterValue}
              className="flex-1 min-w-0 mr-4"
            />
          </span>
        </div>
      </EllipsisRowTooltip>
    ),
  };
  if (item.kind === 'room' && item.zones.length > 0) {
    result.children = item.zones.map((zone) => ({
      key: zone.id,
      label: (
        <EllipsisRowTooltip name={zone.name}>
          <div
            className="flex items-center min-w-0 overflow-hidden"
            onClick={() => updateUrlParams(ctx.navigate, { floorId: floor.id, zoneId: zone.id })}
          >
            <Icon name="zone" className="mr-2" />
            <MiddleTruncatedText
              text={zone.name}
              match={ctx.filterValue}
              className="flex-1 min-w-0 mr-4"
            />
          </div>
        </EllipsisRowTooltip>
      ),
    }));
  }
  return result;
}
